(() => {
  const ready = (fn) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktopToc = () => window.matchMedia('(min-width: 1121px)').matches;

  ready(() => {
    document.querySelectorAll('[data-awja-article]').forEach((articleRoot) => {
      const body = articleRoot.querySelector('[data-awja-body]');
      const tocList = articleRoot.querySelector('[data-awja-toc-list]');
      const tocWrap = articleRoot.querySelector('[data-awja-toc]');

      const refineToc = () => {
        if (!body || !tocList || !tocList.children.length) return;

        body.querySelectorAll('h2[data-awla-toc-exclude], h3[data-awla-toc-exclude]').forEach((heading) => {
          const link = tocList.querySelector(`a[href="#${CSS.escape(heading.id)}"]`);
          link?.closest('li')?.remove();
        });

        body.querySelectorAll('[data-awla-toc-label]').forEach((heading) => {
          const link = tocList.querySelector(`a[href="#${CSS.escape(heading.id)}"]`);
          if (link) link.textContent = heading.dataset.awlaTocLabel;
        });
      };

      let tocSetupAttempts = 0;
      const setupToc = () => {
        refineToc();
        if (!tocWrap || !tocList || !tocList.children.length) {
          tocSetupAttempts += 1;
          if (tocSetupAttempts < 20) window.setTimeout(setupToc, 60);
          return;
        }
        if (tocWrap.dataset.awlTocReady === 'true') return;
        tocWrap.dataset.awlTocReady = 'true';

        const toggle = tocWrap.querySelector('[data-awla-toc-toggle]');
        const panel = tocWrap.querySelector('[data-awla-toc-panel]');
        const currentLabel = tocWrap.querySelector('[data-awla-toc-current]');
        const positionLabel = tocWrap.querySelector('[data-awla-toc-position]');
        const links = [...tocList.querySelectorAll('a')];
        if (!toggle || !panel || !links.length) return;

        tocWrap.classList.add('is-enhanced');

        const setPanelInteractive = (interactive) => {
          panel.setAttribute('aria-hidden', interactive ? 'false' : 'true');
          if ('inert' in panel) panel.inert = !interactive;
          links.forEach((link) => {
            if (interactive) link.removeAttribute('tabindex');
            else link.setAttribute('tabindex', '-1');
          });
        };

        const setOpen = (open, { focusToggle = false } = {}) => {
          const shouldOpen = Boolean(open && desktopToc());
          tocWrap.classList.toggle('is-open', shouldOpen);
          toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
          setPanelInteractive(!desktopToc() || shouldOpen);
          if (focusToggle) toggle.focus({ preventScroll: true });
        };

        const syncCurrent = (preferredLink = null) => {
          const active = preferredLink
            || links.find((link) => link.getAttribute('aria-current') === 'true')
            || links[0];
          const index = Math.max(0, links.indexOf(active));
          if (currentLabel) currentLabel.textContent = active.textContent.trim();
          if (positionLabel) {
            positionLabel.textContent = `${String(index + 1).padStart(2, '0')} / ${String(links.length).padStart(2, '0')}`;
          }
        };

        const syncMode = () => {
          if (desktopToc()) setOpen(false);
          else {
            tocWrap.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            setPanelInteractive(true);
          }
          syncCurrent();
        };

        toggle.addEventListener('click', () => {
          setOpen(!tocWrap.classList.contains('is-open'));
        });

        links.forEach((link) => {
          link.addEventListener('click', (event) => {
            syncCurrent(link);
            setOpen(false);

            if (
              event.defaultPrevented
              || event.button !== 0
              || event.metaKey
              || event.ctrlKey
              || event.shiftKey
              || event.altKey
              || !link.hash
            ) return;

            const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
            if (!target) return;
            event.preventDefault();
            target.scrollIntoView({
              behavior: reducedMotion() ? 'auto' : 'smooth',
              block: 'start'
            });
            try {
              history.pushState(null, '', link.hash);
            } catch (_) {
              // Hash navigation still works even if history is unavailable.
            }
          });
        });

        document.addEventListener('pointerdown', (event) => {
          if (desktopToc() && tocWrap.classList.contains('is-open') && !tocWrap.contains(event.target)) {
            setOpen(false);
          }
        }, { passive: true });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && tocWrap.classList.contains('is-open')) {
            setOpen(false, { focusToggle: true });
          }
        });

        const observer = new MutationObserver(() => syncCurrent());
        observer.observe(tocList, {
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-current']
        });

        window.addEventListener('resize', syncMode, { passive: true });
        window.addEventListener('hashchange', () => {
          const hashLink = links.find((link) => link.hash === window.location.hash);
          syncCurrent(hashLink || null);
        }, { passive: true });

        syncMode();
      };

      refineToc();
      requestAnimationFrame(setupToc);
    });

    document.querySelectorAll('[data-awl-compass]').forEach((compass) => {
      if (compass.dataset.awlReady === 'true') return;
      compass.dataset.awlReady = 'true';

      const tabs = [...compass.querySelectorAll('[data-awl-compass-tab]')];
      const panels = [...compass.querySelectorAll('[data-awl-compass-panel]')];
      if (!tabs.length || !panels.length) return;

      const activate = (id, { focus = false } = {}) => {
        const activeTab = tabs.find((tab) => tab.dataset.awlCompassTab === id);
        const activePanel = panels.find((panel) => panel.dataset.awlCompassPanel === id);
        if (!activeTab || !activePanel) return;

        tabs.forEach((tab) => {
          const selected = tab === activeTab;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });

        panels.forEach((panel) => {
          const selected = panel === activePanel;
          panel.hidden = !selected;
          panel.classList.remove('is-entering');
          if (selected && !reducedMotion()) {
            requestAnimationFrame(() => {
              panel.classList.add('is-entering');
              panel.addEventListener('animationend', () => panel.classList.remove('is-entering'), { once: true });
            });
          }
        });

        if (focus) activeTab.focus({ preventScroll: true });
        activeTab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab.dataset.awlCompassTab));
        tab.addEventListener('keydown', (event) => {
          let targetIndex = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') targetIndex = 0;
          if (event.key === 'End') targetIndex = tabs.length - 1;
          if (targetIndex === null) return;
          event.preventDefault();
          activate(tabs[targetIndex].dataset.awlCompassTab, { focus: true });
        });
        tab.addEventListener('pointerup', (event) => {
          if (event.pointerType !== 'mouse') requestAnimationFrame(() => tab.blur());
        }, { passive: true });
      });
    });

    document.querySelectorAll('.awla-faq').forEach((faq) => {
      const items = [...faq.querySelectorAll('details')];
      items.forEach((item) => {
        if (item.dataset.awlFaqReady === 'true') return;
        item.dataset.awlFaqReady = 'true';

        item.addEventListener('toggle', () => {
          if (!item.open) return;
          items.forEach((other) => {
            if (other !== item && other.open) other.open = false;
          });
        });

        const summary = item.querySelector('summary');
        summary?.addEventListener('pointerup', (event) => {
          if (event.pointerType !== 'mouse') requestAnimationFrame(() => summary.blur());
        }, { passive: true });
      });
    });

    /* Touch links flash light only while pressed and are reset before navigation/BFCache. */
    const touchLinks = [...document.querySelectorAll('.aw-leitartikel .awla-body a')];
    const clearTouchLink = (link, { blur = true } = {}) => {
      if (!link) return;
      const wasTapped = link.classList.contains('is-awl-tapped');
      link.classList.remove('is-awl-tapped');
      if (blur && wasTapped && !link.matches(':focus-visible')) link.blur();
    };
    const clearAllTouchLinks = () => touchLinks.forEach((link) => clearTouchLink(link, { blur: false }));

    touchLinks.forEach((link) => {
      const press = (event) => {
        if (event.pointerType === 'mouse') return;
        clearAllTouchLinks();
        link.classList.add('is-awl-tapped');
      };

      const release = (event) => {
        if (event?.pointerType === 'mouse') return;
        requestAnimationFrame(() => clearTouchLink(link));
      };

      link.addEventListener('pointerdown', press, { passive: true });
      link.addEventListener('pointerup', release, { passive: true });
      link.addEventListener('pointercancel', release, { passive: true });
      link.addEventListener('click', () => clearTouchLink(link));
      link.addEventListener('blur', () => link.classList.remove('is-awl-tapped'), { passive: true });
    });

    window.addEventListener('pagehide', clearAllTouchLinks, { passive: true });
    window.addEventListener('pageshow', clearAllTouchLinks, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') clearAllTouchLinks();
    }, { passive: true });
  });
})();
